import React from 'react';
import SolutionPageTemplate from './SolutionPageTemplate';

// Concrete code examples shown in the right column of each section.
// Keep them small + copyable so the page reads as "you could actually
// do this in 5 minutes", not "here's a marketing screenshot".

const DEPLOY_CODE = `# One-line install + browse
zibby app templates
# → n8n          (workflow automation, $0.05/hr)
# → grafana      (metrics + dashboards,  $0.05/hr)
# → gastown      (multi-agent workspace, $0.05/hr)
# → open-webui   (ChatGPT-style UI,      $0.25/hr)
# → drawio       (diagrams,              $0.05/hr)

# Deploy n8n to your project (private, your own EFS volume)
zibby app deploy n8n --project <id> --name automations
# → Provisioning… ECS service + EFS + ALB target group
# → Deployed (instanceId: a1b2c3d4)
# → Public URL: https://a1b2c3d4.apps.zibby.dev`;

const OPS_CODE = `# Live status (1-line summary)
zibby app status a1b2c3d4
# → automations (n8n v1.97.1)
#     status   running (1/1) ✓
#     cost     $0.05/hr
#     uptime   7d 14h

# Restart, scale, upgrade — all the same surface
zibby app restart  a1b2c3d4
zibby app upgrade  a1b2c3d4       # rolling, zero-downtime
zibby app destroy  a1b2c3d4 --yes # tear it all down

# Live logs (auto-reconnect SSE)
zibby app logs a1b2c3d4 -t`;

const AGENT_OPS_CODE = `# agent-ops — the autonomous sidecar
# Bundled with every app. Every hour the daemon:
#   1. health-checks the app (HTTP probe + container)
#   2. self-heals: container OOM → restart; disk full → prune
#   3. records each run into agent-ops-runs (queryable below)

zibby app activity a1b2c3d4
# → 14:00  hourly_health_check  ok   (1.2s)
# → 13:00  hourly_health_check  ok   (1.1s)
# → 12:04  oom_recovery         ok   (4.8s)
# → 12:00  hourly_health_check  warn (container restarting)`;

const AppsPage = () => {
  return (
    <SolutionPageTemplate
      meta={{
        title: 'Zibby Apps — one-click hosted open-source instances with agent operators',
        description:
          'Deploy n8n, Grafana, Open WebUI and more as private hosted instances. Each app comes with an agent-ops sidecar that handles health checks, self-healing, and upgrades autonomously.',
      }}
      eyebrow="PLATFORM"
      title="Hosted apps that operate themselves."
      lede="One-click instances of n8n, Grafana, Open WebUI, draw.io, and more — each private to your project, with an autonomous agent-ops sidecar that handles health checks, restarts, and upgrades. The hosting cost of self-managed. The operational cost of zero."
      ctaPrimary={{ label: 'Browse the marketplace', href: '/marketplace/apps' }}
      ctaSecondary={{ label: 'Read the docs →', href: 'https://docs.zibby.app', external: true }}
      sections={[
        {
          title: 'Deploy in 30 seconds, not an afternoon.',
          body: (
            <>
              <p>
                Every app in the marketplace is a curated, opinionated bundle: container image, EFS volume layout, ALB wiring, secrets pattern. <strong>Pick what you want, name it, done.</strong> No Helm charts, no Terraform modules, no DNS dance.
              </p>
              <p>
                Each instance gets its own subdomain (<code style={{ background: 'rgba(255,255,255,0.06)', padding: '2px 6px', borderRadius: 4, fontFamily: 'SF Mono, Menlo, monospace', fontSize: '13px' }}>&lt;id&gt;.apps.zibby.dev</code>) and a dedicated EFS volume so your data is private to <em>you</em> — not shared multi-tenant, not pooled with anyone else's runtime.
              </p>
            </>
          ),
          code: DEPLOY_CODE,
        },
        {
          title: 'Operate by command, not by ticket.',
          body: (
            <>
              <p>
                Restart, scale, upgrade, tear down — every operation is one CLI call. Rolling deploys are the default; the ALB drains old tasks before the new one accepts traffic, so a <code style={{ background: 'rgba(255,255,255,0.06)', padding: '2px 6px', borderRadius: 4, fontFamily: 'SF Mono, Menlo, monospace', fontSize: '13px' }}>zibby app upgrade</code> on a load-bearing n8n is <strong>zero-downtime by default</strong>.
              </p>
              <p>
                Per-minute billing — pause it at midnight, resume in the morning, pay only for the minutes it's actually running.
              </p>
            </>
          ),
          code: OPS_CODE,
        },
        {
          title: 'The agent-ops sidecar — autonomous, by design.',
          body: (
            <>
              <p>
                Every Zibby app ships with <strong>agent-ops</strong>, a small daemon sidecar that runs alongside the app container. It does what a human operator would do — only it does it every hour, never sleeps, and never forgets to file a runbook.
              </p>
              <ul>
                <li><strong>Hourly health checks</strong> — HTTP probe + container state, recorded as queryable run records (not just logs)</li>
                <li><strong>Self-healing</strong> — container OOM → automatic restart; disk pressure → prune; bad release → roll back to last good</li>
                <li><strong>Upgrade orchestration</strong> — when a new app version lands in the catalog, agent-ops can run the in-place upgrade flow on a schedule you set</li>
                <li><strong>Activity log</strong> — every action the daemon takes is one row in the app's Agent activity tab, with structured fields you can grep / chart</li>
              </ul>
            </>
          ),
          code: AGENT_OPS_CODE,
        },
        {
          title: 'Production primitives, not a "deploy button" demo.',
          body: (
            <>
              <p>The hosting isn't an afterthought — every Zibby app ships with:</p>
              <ul>
                <li><strong>Per-instance EFS volume</strong> — persistent, encrypted-at-rest, survives container restarts and upgrades</li>
                <li><strong>Per-instance ALB target group</strong> — your traffic doesn't share a load balancer with other tenants</li>
                <li><strong>Per-minute Fargate billing</strong> — pay-as-you-run, including agent-ops sidecar</li>
                <li><strong>SSE log streaming</strong> with auto-reconnect — tail any container from anywhere</li>
                <li><strong>Dedicated egress IP</strong> addon — pin all outbound HTTPS through one whitelistable IP for self-hosted GitLab / Salesforce / Oracle Cloud</li>
                <li><strong>Resource overrides</strong> — bump CPU / memory per instance without leaving the catalog</li>
              </ul>
            </>
          ),
        },
        {
          title: 'A growing catalog of opinionated bundles.',
          body: (
            <>
              <p>
                We curate apps that <em>actually deserve hosting</em> — multi-user, stateful, persistent-volume sort of tools — not toy single-page web demos.
              </p>
              <table className="sp-table">
                <thead>
                  <tr><th>App</th><th>Category</th><th>Tier</th><th>Rate</th></tr>
                </thead>
                <tbody>
                  <tr><td><strong>n8n</strong></td><td>Workflow automation</td><td>Light</td><td>$0.05/hr</td></tr>
                  <tr><td><strong>Grafana</strong></td><td>Metrics + dashboards</td><td>Light</td><td>$0.05/hr</td></tr>
                  <tr><td><strong>Gas Town</strong></td><td>Multi-agent workspace</td><td>Light</td><td>$0.05/hr</td></tr>
                  <tr><td><strong>draw.io</strong></td><td>Diagrams + flowcharts</td><td>Light</td><td>$0.05/hr</td></tr>
                  <tr><td><strong>Open WebUI</strong></td><td>ChatGPT-style UI for Ollama</td><td>Heavy</td><td>$0.25/hr</td></tr>
                </tbody>
              </table>
              <p style={{ marginTop: 24 }}>
                More apps land monthly. Suggest one in the marketplace and we'll bundle it.
              </p>
            </>
          ),
        },
      ]}
      cta={{
        title: 'Deploy your first app in 30 seconds.',
        body: 'No setup. No credit card. $10 in free credits — enough to run a Light app for ~8 days.',
        primary: { label: 'Browse the marketplace', href: '/marketplace/apps' },
        secondary: { label: 'Read the docs →', href: 'https://docs.zibby.app', external: true },
      }}
    />
  );
};

export default AppsPage;
